import type { Uuid } from "spacetimedb";
import { useState } from "react";

import { ActionCard } from "@/web/routes/_protected/smb/-dashboard/-ActionCard";
import { UploadLeaseDocDrawer } from "@/web/routes/_protected/smb/-dashboard/-actions/UploadLeaseDocDrawer";
import type {
  SmbActionCoreProps,
  SmbActionDefinition,
} from "@/web/routes/_protected/smb/-dashboard/-types";

type UploadLeaseDocProps = {
  /** Underwritable entity that will own the resulting lease document rows. */
  underwritableEntity: Uuid;
};

function UploadLeaseDocAction({
  underwritableEntity,
  onSkip,
  onComplete,
}: UploadLeaseDocProps & SmbActionCoreProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <ActionCard category="negotiate">
        <ActionCard.Header>
          <ActionCard.Badge>Price</ActionCard.Badge>
          <ActionCard.DetailsTrigger />
        </ActionCard.Header>
        <ActionCard.Main>
          <ActionCard.Title>Upload lease(s)</ActionCard.Title>
          <ActionCard.Description>
            We use your rent to calculate what the building is worth today.
          </ActionCard.Description>
        </ActionCard.Main>
        <ActionCard.Footer>
          <ActionCard.SkipButton onSkip={onSkip} />
          <ActionCard.PrimaryButton onClick={() => setOpen(true)}>Upload</ActionCard.PrimaryButton>
        </ActionCard.Footer>
        <ActionCard.Back title="Why we ask">
          <p>
            Lease terms drive a huge share of your business's economics — rent schedule, expiration,
            renewal options, purchase rights. Sharing the document lets us extract those terms
            directly instead of asking you to type them in.
          </p>
        </ActionCard.Back>
      </ActionCard>

      <UploadLeaseDocDrawer
        open={open}
        onOpenChange={setOpen}
        underwritableEntity={underwritableEntity}
        onUploaded={onComplete}
      />
    </>
  );
}

export function createUploadLeaseDocAction(
  props: UploadLeaseDocProps & { id?: string; canSkip?: boolean },
): SmbActionDefinition {
  const { id = "upload-lease-doc", canSkip = true, ...rest } = props;
  return {
    id,
    category: "negotiate",
    canSkip,
    render: (core) => <UploadLeaseDocAction {...rest} {...core} />,
  };
}

// ── Engine rules ─────────────────────────────────────────────────────────────
import type { LeaseConnection } from "@tycho/web-bindings/types";
import { CardRulesEngine } from "@/web/routes/_protected/smb/-dashboard/-CardRulesEngine";
import { createUploadLeaseDocCompleted } from "@/web/routes/_protected/smb/-dashboard/-completedActions/UploadLeaseDocCompleted";

export const uploadLeaseDocRule = CardRulesEngine.singletonRule({
  id: "upload-lease-doc",
  priority: 45,
  when: CardRulesEngine.missingConnection("leases"),
  build: (d) => createUploadLeaseDocAction({ underwritableEntity: d.underwritableEntityId }),
});

// One completed card representing the whole batch of uploaded leases; users
// can still add more via the card's primary action. Fires once per uwEntity
// when ≥1 lease doc is connected (the opposite of `missingConnection`).
export const uploadLeaseDocCompletedRule = CardRulesEngine.singletonCompletedRule({
  id: "upload-lease-doc:done",
  when: CardRulesEngine.not(CardRulesEngine.missingConnection("leases")),
  build: (d) =>
    createUploadLeaseDocCompleted({
      underwritableEntity: d.underwritableEntityId,
      leases: d.financialConnections.leases satisfies LeaseConnection[],
    }),
});
